import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import PensionForm from "@/components/PensionForm";
import ResultPanel from "@/components/ResultPanel";
import SEO from "@/components/SEO";
import { paramsToState, buildShareUrl } from "@/utils/urlState";
import { ArrowDown } from "lucide-react";

const defaultData = {
  method: "cnap",
  current_age: 35,
  retirement_age: 65,
  career_start_age: 23,
  education_years: 0,
  monthly_gross_salary: 5500,
  past_salary_growth: 2.0,
  future_salary_growth: 1.5,
  tax_class: "1",
  children: 0,
};

const CalculatorPage = () => {
  const { t, API, authHeaders, lang } = useApp();
  const [data, setData] = useState(() => paramsToState(window.location.search, defaultData));
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [label, setLabel] = useState("");

  const handleShare = async () => {
    const url = buildShareUrl(data);
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t.nav.shareCopied || "Link copied", { description: url });
    } catch {
      window.prompt("Copy link:", url);
    }
  };

  const handleCalculate = async () => {
    setCalculating(true);
    try {
      const toNum = (v) => {
        if (v === "" || v === null || v === undefined) return 0;
        const s = String(v).replace(",", ".");
        const n = parseFloat(s);
        return Number.isFinite(n) ? n : 0;
      };
      const payload = {
        method: data.method,
        current_age: Math.trunc(toNum(data.current_age)),
        retirement_age: Math.trunc(toNum(data.retirement_age)),
        career_start_age: Math.trunc(toNum(data.career_start_age)),
        education_years: Math.trunc(toNum(data.education_years)),
        monthly_gross_salary: toNum(data.monthly_gross_salary),
        past_salary_growth: toNum(data.past_salary_growth),
        future_salary_growth: toNum(data.future_salary_growth),
        tax_class: data.tax_class,
        children: Math.trunc(toNum(data.children)),
      };
      const res = await axios.post(`${API}/pension/calculate`, payload);
      setResult(res.data);
      toast.success("Calcul effectué", { description: `${res.data.net_monthly_pension.toFixed(2)} €/mois net` });
    } catch (e) {
      toast.error("Erreur de calcul", { description: e.response?.data?.detail?.toString() || e.message });
    } finally {
      setCalculating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const toNum = (v) => {
        if (v === "" || v === null || v === undefined) return 0;
        const s = String(v).replace(",", ".");
        const n = parseFloat(s);
        return Number.isFinite(n) ? n : 0;
      };
      const payload = {
        method: data.method,
        current_age: Math.trunc(toNum(data.current_age)),
        retirement_age: Math.trunc(toNum(data.retirement_age)),
        career_start_age: Math.trunc(toNum(data.career_start_age)),
        education_years: Math.trunc(toNum(data.education_years)),
        monthly_gross_salary: toNum(data.monthly_gross_salary),
        past_salary_growth: toNum(data.past_salary_growth),
        future_salary_growth: toNum(data.future_salary_growth),
        tax_class: data.tax_class,
        children: Math.trunc(toNum(data.children)),
        name: label || undefined,
      };
      await axios.post(`${API}/scenarios`, payload, authHeaders());
      toast.success(t.result.saved);
      setLabel("");
    } catch (e) {
      toast.error("Erreur", { description: e.response?.data?.detail?.toString() || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <SEO
        title={t.appName}
        description={t.tagline}
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebApplication",
              "name": "Lux·Pension — Luxembourg Pension Estimator",
              "applicationCategory": "FinanceApplication",
              "operatingSystem": "Any",
              "description": t.tagline,
              "url": "https://pensioncheck.online/",
              "inLanguage": lang,
              "offers": { "@type": "Offer", "price": "0", "priceCurrency": "EUR" },
            },
            {
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": lang === "fr" ? "Comment est calculée la pension au Luxembourg ?" : lang === "de" ? "Wie wird die Rente in Luxemburg berechnet?" : "How is the Luxembourg pension calculated?",
                  "acceptedAnswer": { "@type": "Answer", "text": lang === "fr" ? "La CNAP additionne une majoration forfaitaire (selon la durée de carrière) et une majoration proportionnelle (~1,85% des salaires cotisés)." : lang === "de" ? "Die CNAP addiert einen pauschalen Zuschlag (nach Karrieredauer) und einen proportionalen Zuschlag (~1,85% der Beiträge)." : "CNAP adds a flat increment (based on career length) and a proportional increment (~1.85% of lifetime contributions)." }
                },
                {
                  "@type": "Question",
                  "name": lang === "fr" ? "Les frontaliers ont-ils droit à une pension au Luxembourg ?" : lang === "de" ? "Haben Grenzgänger Anspruch auf eine luxemburgische Rente?" : "Are cross-border workers entitled to a Luxembourg pension?",
                  "acceptedAnswer": { "@type": "Answer", "text": lang === "fr" ? "Oui, chaque année cotisée au Luxembourg ouvre des droits à pension CNAP, versés à vie même à l'étranger via le règlement européen 883/2004." : lang === "de" ? "Ja, jedes in Luxemburg gezahlte Beitragsjahr begründet CNAP-Rentenansprüche, die lebenslang gezahlt werden – auch im Ausland (EU-VO 883/2004)." : "Yes — every year contributed in Luxembourg builds CNAP rights, paid for life including abroad under EU regulation 883/2004." }
                },
                {
                  "@type": "Question",
                  "name": lang === "fr" ? "Les études supérieures comptent-elles pour la pension ?" : lang === "de" ? "Zählen Hochschuljahre für die Rente?" : "Do higher-education years count toward the pension?",
                  "acceptedAnswer": { "@type": "Answer", "text": lang === "fr" ? "Oui, comme périodes complémentaires (entre 18 et 27 ans), elles allongent la durée totale créditée sans ajouter au cumul des cotisations." : lang === "de" ? "Ja, als Ergänzungszeiten (18–27) verlängern sie die anerkannte Gesamtdauer, ohne das Beitragsvolumen zu erhöhen." : "Yes — as complementary periods (ages 18–27), they extend the recognized duration without adding to lifetime contributions." }
                }
              ]
            }
          ]
        }}
      />
      {/* Hero */}
      <section className="hero-bg text-white" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-24 md:py-32">
          <div className="max-w-3xl">
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-blue-200 mb-6">
              · Luxembourg · CNAP · 2025 ·
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              {t.appName}
            </h1>
            <p className="font-sans text-base md:text-lg text-slate-200 mt-6 max-w-2xl leading-relaxed">
              {t.tagline}
            </p>
            <a href="#calculator" data-testid="cta-scroll" className="inline-flex items-center gap-3 mt-10 px-6 py-3 bg-white text-slate-900 text-xs font-mono uppercase tracking-[0.2em] hover:bg-slate-100">
              {t.cta} <ArrowDown size={14} strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </section>

      {/* Calculator */}
      <section id="calculator" className="max-w-7xl mx-auto px-6 md:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            <PensionForm
              data={data}
              setData={setData}
              onCalculate={handleCalculate}
              calculating={calculating}
            />
          </div>
          <div className="lg:col-span-2">
            <ResultPanel
              result={result}
              onSave={handleSave}
              onShare={handleShare}
              scenarioLabel={label}
              setScenarioLabel={setLabel}
              saving={saving}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default CalculatorPage;
